import { defineStore } from 'pinia'
// import type { Messaging } from 'firebase/messaging'

interface PushNotification {
  id: string
  title: string
  body: string
  icon?: string
  badge?: string
  data: Record<string, any>
  timestamp: string
  read: boolean
}

interface ScheduledNotification {
  id: string
  title: string
  body: string
  scheduledFor: string
  status: 'pending' | 'sent' | 'failed'
  data?: Record<string, any>
}

interface PushNotificationState {
  // Estado da permissão
  permission: NotificationPermission | null

  // Token FCM
  fcmToken: string | null
  tokenLastUpdated: string | null

  // Configurações
  enabled: boolean
  soundEnabled: boolean
  vibrationEnabled: boolean

  // Histórico de notificações
  notifications: PushNotification[]
  unreadCount: number

  // Notificações agendadas
  scheduledNotifications: ScheduledNotification[]

  // Estado de carregamento
  isLoading: boolean
  error: string | null

  // Controle do modal de opt-in
  optInModalVisible: boolean
  optInAttempts: number
  lastOptInPrompt: string | null
  optInSuppressedUntil: string | null
  userExplicitlyDeclined: boolean

  // Controle de inicialização
  isInitialized: boolean
  isInitializing: boolean
  welcomeNotificationSent: boolean
}

export const usePushNotificationsStore = defineStore('pushNotifications', {
  state: (): PushNotificationState => ({
    permission: null,
    fcmToken: null,
    tokenLastUpdated: null,
    enabled: false,
    soundEnabled: true,
    vibrationEnabled: true,
    notifications: [],
    unreadCount: 0,
    scheduledNotifications: [],
    isLoading: false,
    error: null,
    optInModalVisible: false,
    optInAttempts: 0,
    lastOptInPrompt: null,
    optInSuppressedUntil: null,
    userExplicitlyDeclined: false,
    isInitialized: false,
    isInitializing: false,
    welcomeNotificationSent: false,
  }),

  getters: {
    // Verifica se as notificações estão disponíveis
    isAvailable (): boolean {
      return typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator
    },

    // Verifica se tem permissão
    hasPermission (): boolean {
      return this.permission === 'granted'
    },

    // Pega notificações não lidas
    unreadNotifications (): PushNotification[] {
      return this.notifications.filter(n => !n.read)
    },

    // Pega notificações agendadas pendentes
    pendingScheduled (): ScheduledNotification[] {
      return this.scheduledNotifications.filter(n => n.status === 'pending')
    },
  },

  actions: {
    // Inicializa a store
    async initialize () {
      // Evita múltiplas inicializações simultâneas
      if (this.isInitialized || this.isInitializing) {
        return
      }

      if (!this.isAvailable) {
        this.error = 'Notificações não são suportadas neste navegador'
        return
      }

      this.isInitializing = true
      this.isLoading = true

      try {
        // Limpa configurações antigas do localStorage
        const oldKeys = ['pushNotifications.enabled', 'pushNotifications.soundEnabled', 'pushNotifications.vibrationEnabled']
        oldKeys.forEach((key) => {
          try {
            localStorage.removeItem(key)
          } catch (e) {
            // Ignora erros de localStorage
          }
        })

        // Verifica se Firebase foi inicializado corretamente
        const nuxtApp = useNuxtApp()
        if (nuxtApp.$error) {
          this.error = `Erro do Firebase: ${nuxtApp.$error} - ${nuxtApp.$errorDetails || ''}`
          return
        }

        // Verifica permissão atual
        this.permission = Notification.permission

        // Carrega configurações do backend
        await this.loadSettingsFromBackend()

        // Se já tem permissão do navegador E as notificações estão habilitadas, registra o token
        if (this.hasPermission && this.enabled) {
          await this.registerForPushNotifications()
        }

        // Carrega notificações do localStorage ou API
        await this.loadNotificationHistory()

        // Escuta mensagens do service worker
        this.listenToServiceWorkerMessages()

        this.isInitialized = true
      } catch (error) {
        console.error('Erro ao inicializar push notifications:', error)
        this.error = 'Erro ao inicializar notificações'
      } finally {
        this.isLoading = false
        this.isInitializing = false
      }
    },

    // Carrega configurações do backend
    async loadSettingsFromBackend () {
      const apiStore = useApiStore()
      try {
        const response = await apiStore.request('/push-notifications/settings', {
          method: 'GET',
        })

        if ((response as any)?.data) {
          const data = (response as any).data
          // Só atualiza se existir configuração no backend
          // Mantém os valores atuais se não houver configuração
          if (data.enabled !== undefined) {
            this.enabled = data.enabled
          }
          if (data.soundEnabled !== undefined) {
            this.soundEnabled = data.soundEnabled
          }
          if (data.vibrationEnabled !== undefined) {
            this.vibrationEnabled = data.vibrationEnabled
          }
        }
      } catch (error) {
        console.error('Erro ao carregar configurações do backend:', error)
        // Em caso de erro, mantém as configurações locais
      }
    },

    // Solicita permissão e registra para notificações
    async requestPermission () {
      if (!this.isAvailable) {
        throw new Error('Notificações não são suportadas')
      }

      this.isLoading = true
      this.error = null

      try {
        const permission = await Notification.requestPermission()
        this.permission = permission

        if (permission === 'granted') {
          await this.registerForPushNotifications()
          this.enabled = true

          // Salva configuração no backend
          await this.updateSettings({})
          return true
        } else {
          this.enabled = false
          this.error = permission === 'denied'
            ? 'Permissão negada. Verifique as configurações do navegador.'
            : 'Permissão não concedida'
          return false
        }
      } catch (error) {
        console.error('Erro ao solicitar permissão:', error)
        this.error = 'Erro ao solicitar permissão'
        return false
      } finally {
        this.isLoading = false
      }
    },

    // Registra para receber push notifications
    async registerForPushNotifications () {
      const nuxtApp = useNuxtApp()

      if (!nuxtApp.$getFCMToken) {
        console.error('$getFCMToken não está disponível no Nuxt')
        throw new Error('Firebase não está inicializado. Verifique se o plugin firebase.client.ts está carregado.')
      }

      try {
        // Verifica se o service worker está registrado
        if ('serviceWorker' in navigator) {
          const registration = await navigator.serviceWorker.ready

          if (!registration.pushManager) {
            throw new Error('PushManager não está disponível no service worker.')
          }
        } else {
          throw new Error('Service Worker não é suportado neste navegador.')
        }

        // Obtém o token FCM
        const token = await nuxtApp.$getFCMToken()

        if (token) {
          this.fcmToken = token
          this.tokenLastUpdated = new Date().toISOString()

          // Envia o token para o backend
          await this.sendTokenToBackend(token)
        } else {
          throw new Error('Não foi possível obter o token FCM. Verifique as permissões do navegador e se o service worker está funcionando.')
        }
      } catch (error) {
        console.error('Erro ao registrar para notificações:', error)
        throw error
      }
    },

    // Envia o token para o backend
    async sendTokenToBackend (token: string) {
      const apiStore = useApiStore()

      try {
        const response = await apiStore.request('/push-notifications/register', {
          method: 'POST',
          body: {
            token,
            platform: 'web',
            device_info: {
              userAgent: navigator.userAgent,
              language: navigator.language,
              platform: navigator.platform,
            },
          },
        })

        return response
      } catch (error) {
        console.error('Erro ao enviar token para o backend:', error)
        throw error
      }
    },

    // Desabilita notificações
    async disableNotifications () {
      this.enabled = false

      // Salva configuração no backend
      const apiStore = useApiStore()
      try {
        const payload = {
          enabled: false,
          soundEnabled: this.soundEnabled,
          vibrationEnabled: this.vibrationEnabled,
        }

        await apiStore.request('/push-notifications/settings', {
          method: 'POST',
          body: payload,
        })
      } catch (error) {
        console.error('Erro ao desabilitar notificações:', error)
        throw error
      }
    },

    // Adiciona notificação ao histórico
    addNotificationToHistory (notification: Omit<PushNotification, 'read'>) {
      const newNotification: PushNotification = {
        ...notification,
        read: false,
      }

      this.notifications.unshift(newNotification)
      this.unreadCount++

      // Limita o histórico a 100 notificações
      if (this.notifications.length > 100) {
        this.notifications = this.notifications.slice(0, 100)
      }

      // Salva no localStorage
      this.saveNotificationHistory()
    },

    // Marca notificação como lida
    markAsRead (notificationId: string) {
      const notification = this.notifications.find(n => n.id === notificationId)
      if (notification && !notification.read) {
        notification.read = true
        this.unreadCount = Math.max(0, this.unreadCount - 1)
        this.saveNotificationHistory()
      }
    },

    // Marca todas como lidas
    markAllAsRead () {
      this.notifications.forEach((n) => {
        n.read = true
      })
      this.unreadCount = 0
      this.saveNotificationHistory()
    },

    // Limpa histórico de notificações
    clearHistory () {
      this.notifications = []
      this.unreadCount = 0
      this.saveNotificationHistory()
    },

    // Agenda uma notificação
    async scheduleNotification (
      title: string,
      body: string,
      scheduledFor: Date | string,
      data?: Record<string, any>
    ) {
      const apiStore = useApiStore()

      const scheduled: ScheduledNotification = {
        id: Date.now().toString(),
        title,
        body,
        scheduledFor: typeof scheduledFor === 'string' ? scheduledFor : scheduledFor.toISOString(),
        status: 'pending',
        data,
      }

      try {
        // Envia para o backend agendar
        const response = await apiStore.request('/push-notifications/schedule', {
          method: 'POST',
          body: {
            title,
            body,
            scheduled_for: scheduled.scheduledFor,
            data,
          },
        })

        scheduled.id = response?.id || scheduled.id
        this.scheduledNotifications.push(scheduled)

        return scheduled
      } catch (error) {
        console.error('Erro ao agendar notificação:', error)
        throw error
      }
    },

    // Cancela notificação agendada
    async cancelScheduledNotification (notificationId: string) {
      const apiStore = useApiStore()

      try {
        await apiStore.request(`/push-notifications/scheduled/${notificationId}`, {
          method: 'DELETE',
        })

        const index = this.scheduledNotifications.findIndex(n => n.id === notificationId)
        if (index > -1) {
          this.scheduledNotifications.splice(index, 1)
        }
      } catch (error) {
        console.error('Erro ao cancelar notificação agendada:', error)
        throw error
      }
    },

    // Testa envio de notificação
    async testNotification () {
      if (!this.hasPermission) {
        throw new Error('Notificações não estão habilitadas. Por favor, permita as notificações primeiro.')
      }

      try {
        // Primeiro, tenta enviar via backend/Firebase se tiver token FCM
        if (this.fcmToken) {
          const apiStore = useApiStore()
          const response = await apiStore.request('/push-notifications/test', {
            method: 'POST',
          })

          // Se o backend responder com sucesso, a notificação virá via Firebase
          console.log('Notificação enviada via Firebase:', response)
        } else {
          // Se não tiver token FCM, mostra notificação local
          console.warn('Token FCM não disponível, mostrando notificação local')

          // Adiciona notificação de teste ao histórico
          this.addNotificationToHistory({
            id: Date.now().toString(),
            title: 'Notificação de Teste',
            body: 'Esta é uma notificação de teste do sistema (local)',
            data: { test: true, local: true },
            timestamp: new Date().toISOString(),
          })

          // Mostra notificação local no browser
          if (Notification.permission === 'granted') {
            const notificationOptions: NotificationOptions = {
              body: 'Esta é uma notificação de teste do sistema (local)',
              icon: '/icon-192x192.png',
              badge: '/icon-72x72.png',
              tag: 'test-notification',
              requireInteraction: false,
              // Aplica configurações do usuário
              vibrate: this.vibrationEnabled ? [200, 100, 200] : [],
              silent: !this.soundEnabled,
            }

            const notification = new Notification('Notificação de Teste', notificationOptions)

            // Auto-fecha após 5 segundos
            setTimeout(() => notification.close(), 5000)
          }
        }
      } catch (error) {
        console.error('Erro ao enviar notificação de teste:', error)
        throw error
      }
    },

    // Envia notificação de boas-vindas
    async sendWelcomeNotification () {
      // Verifica se já foi enviada para evitar duplicação
      if (this.welcomeNotificationSent) {
        return
      }

      if (!this.hasPermission) {
        throw new Error('Notificações não estão habilitadas.')
      }

      try {
        // Verifica se precisa renovar o token antes de enviar
        if (!this.fcmToken) {
          const { $getFCMToken } = useNuxtApp()
          const newToken = await $getFCMToken()

          if (newToken) {
            this.fcmToken = newToken
            this.tokenLastUpdated = new Date().toISOString()
            await this.sendTokenToBackend(newToken)
          } else {
            throw new Error('Não foi possível obter token FCM')
          }
        }

        const apiStore = useApiStore()
        const response = await apiStore.request('/push-notifications/send', {
          method: 'POST',
          body: {
            user_id: 'current', // Backend deve interpretar como usuário atual
            title: '🎉 Bem vindo ao LDA!',
            body: 'Você receberá alertas importantes sobre atividades do cartório em tempo real.',
            data: {
              type: 'welcome',
              route: '/atividades',
              welcome: 'true',
            },
          },
        })

        console.log('Notificação de boas-vindas enviada:', response)

        // Marca como enviada para evitar duplicação
        this.welcomeNotificationSent = true

        return response
      } catch (error) {
        console.error('Erro ao enviar notificação de boas-vindas:', error)
        throw error
      }
    },

    // Carrega histórico de notificações
    async loadNotificationHistory () {
      // Tenta carregar do localStorage primeiro
      const stored = localStorage.getItem('push_notifications_history')
      if (stored) {
        try {
          const data = JSON.parse(stored)
          this.notifications = data.notifications || []
          this.unreadCount = data.unreadCount || 0
        } catch (error) {
          console.error('Erro ao carregar histórico do localStorage:', error)
        }
      }

      // Opcionalmente, sincroniza com o backend
      const apiStore = useApiStore()
      try {
        const response = await apiStore.request('/push-notifications/history', {
          method: 'GET',
        })
        if (response && response.notifications) {
          this.notifications = response.notifications
          this.unreadCount = response.unread_count || 0
          this.saveNotificationHistory()
        }
      } catch (error) {
        console.error('Erro ao carregar histórico do backend:', error)
      }
    },

    // Salva histórico no localStorage
    saveNotificationHistory () {
      try {
        localStorage.setItem('push_notifications_history', JSON.stringify({
          notifications: this.notifications,
          unreadCount: this.unreadCount,
          lastUpdated: new Date().toISOString(),
        }))
      } catch (error) {
        console.error('Erro ao salvar histórico no localStorage:', error)
      }
    },

    // Escuta mensagens do service worker
    listenToServiceWorkerMessages () {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.addEventListener('message', (event) => {
          console.log('[pushNotifications.ts] Mensagem do service worker recebida:', event.data)

          if (event.data.type === 'NOTIFICATION_CLICK') {
            // Usa navegateToFromSW para navegar com o router do Nuxt
            // this.navigateToFromSW(event.data.route, event.data.data)
            window.open(event.data.route, '_blank')
          } else if (event.data.type === 'PUSH_RECEIVED') {
            // Atualiza histórico quando notificação é recebida em background
            if (event.data.notification) {
              this.addNotificationToHistory({
                id: Date.now().toString(),
                title: event.data.notification.title,
                body: event.data.notification.body,
                data: event.data.notification.data || {},
                timestamp: new Date().toISOString(),
              })

              useAtividadesStore().carregarResumo()
            }
          }
        })
      }
    },

    // Notifica a store de atividades para atualizar quando push chegar
    notifyActivitiesUpdate (notification: any) {
      try {
        // Busca por atividades relacionadas no data da notificação
        const isActivityRelated = notification.data?.type === 'activity' ||
                                 notification.data?.receita_id ||
                                 notification.data?.despesa_id ||
                                 notification.title?.toLowerCase().includes('receita') ||
                                 notification.title?.toLowerCase().includes('despesa')

        if (isActivityRelated) {
          console.log('[pushNotifications.ts] Notificação relacionada a atividades, solicitando atualização')

          // Tenta acessar a store de atividades se estiver disponível
          try {
            const atividadesStore = useAtividadesStore()
            atividadesStore.carregarResumo()
          } catch (error) {
            console.log('[pushNotifications.ts] Store de atividades não disponível ainda:', error)
            // Se a store não estiver disponível, emite um evento customizado
            // que pode ser capturado pelo widget quando ele for montado
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('pushNotificationReceived', {
                detail: { notification, shouldUpdateActivities: true },
              }))
            }
          }
        }
      } catch (error) {
        console.error('[pushNotifications.ts] Erro ao notificar atualização de atividades:', error)
      }
    },

    // Navega para rota especificada pelo service worker
    navigateToFromSW (route: string, notificationData?: Record<string, any>) {
      try {
        console.log('[pushNotifications.ts] Navegando para:', route, 'com dados:', notificationData)

        // Extrai o deep_link se disponível, senão usa o route
        const targetUrl = notificationData?.deep_link || route

        console.log('[pushNotifications.ts] URL alvo:', targetUrl)

        // Sempre abre em nova aba
        window.open(targetUrl, '_blank')

        // Se tiver dados específicos, pode processar aqui
        if (notificationData) {
          console.log('[pushNotifications.ts] Dados da notificação para processamento:', notificationData)

          // O processamento de modais agora é feito via query parameters no deep_link
          // Ex: /prestacao-de-contas?receitaId=123&abrirModal=true
        }
      } catch (error) {
        console.error('[pushNotifications.ts] Erro ao navegar:', error)
        // Fallback final
        window.location.href = route
      }
    },

    // Atualiza configurações
    async updateSettings (settings: Partial<Pick<PushNotificationState, 'soundEnabled' | 'vibrationEnabled'>>) {
      Object.assign(this, settings)

      // Salva configurações no backend
      const apiStore = useApiStore()
      try {
        await apiStore.request('/push-notifications/settings', {
          method: 'POST',
          body: {
            enabled: this.enabled,
            soundEnabled: this.soundEnabled,
            vibrationEnabled: this.vibrationEnabled,
          },
        })
      } catch (error) {
        console.error('Erro ao salvar configurações:', error)
        throw error
      }
    },

    // Verifica se deve mostrar o modal de opt-in
    shouldShowOptInModal (): boolean {
      // Não mostrar se não está disponível
      if (!this.isAvailable) { return false }

      // Não mostrar se já tem permissão ou foi negada
      if (this.permission === 'granted' || this.permission === 'denied') { return false }

      // Não mostrar se modal já está visível
      if (this.optInModalVisible) { return false }

      // Não mostrar se usuário recusou explicitamente
      if (this.userExplicitlyDeclined) { return false }

      // Não mostrar se está suprimido por 30 dias
      if (this.optInSuppressedUntil) {
        const suppressedUntil = new Date(this.optInSuppressedUntil)
        const now = new Date()
        if (now < suppressedUntil) {
          return false
        }
        // Se o período de supressão expirou, limpa o timestamp
        this.optInSuppressedUntil = null
      }

      // Se nunca interagiu com o modal, sempre mostrar
      if (!this.lastOptInPrompt) { return true }

      // Verificar frequência - máximo 1x por hora se ainda não decidiu
      if (this.lastOptInPrompt) {
        const lastPrompt = new Date(this.lastOptInPrompt)
        const now = new Date()
        const minutesSinceLastPrompt = (now.getTime() - lastPrompt.getTime()) / (1000 * 60)

        // Se passou menos de 60 minutos, não mostrar
        if (minutesSinceLastPrompt < 60) {
          return false
        }
      }

      // Limitar número de tentativas de interação (quando clica em "Ativar")
      if (this.optInAttempts >= 5) {
        return false
      }

      return true
    },

    // Mostra o modal de opt-in
    showOptInModal () {
      if (this.shouldShowOptInModal()) {
        this.optInModalVisible = true
        // Só incrementa tentativas e atualiza timestamp quando o usuário
        // realmente interage com a permissão do navegador
      }
    },

    // Registra que o usuário interagiu com o modal
    recordOptInInteraction () {
      this.optInAttempts++
      this.lastOptInPrompt = new Date().toISOString()
    },

    // Esconde o modal de opt-in
    hideOptInModal () {
      this.optInModalVisible = false
    },

    // Suprime o modal por 30 dias
    suppressOptInFor30Days () {
      const now = new Date()
      const suppressUntil = new Date(now.getTime() + (30 * 24 * 60 * 60 * 1000)) // 30 dias
      this.optInSuppressedUntil = suppressUntil.toISOString()
      this.hideOptInModal()
    },

    // Marca que o usuário recusou explicitamente as notificações
    declineNotificationsPermanently () {
      this.userExplicitlyDeclined = true
      this.hideOptInModal()
    },

    // Reseta a decisão do opt-in (usado na página de configurações)
    resetOptInDecision () {
      this.userExplicitlyDeclined = false
      this.optInSuppressedUntil = null
      this.lastOptInPrompt = null
      this.optInAttempts = 0
    },
  },

  persist: {
    storage: persistedState.localStorage,
    paths: ['permission', 'optInAttempts', 'lastOptInPrompt', 'optInSuppressedUntil', 'userExplicitlyDeclined'],
  },
})
